import { flowRight, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { connect } from '../../../common/components/runtime-context';
import { EXPERIMENT_SNAPPY_TURBO } from '../../constants/experiments';
import { PinIcon } from '../icons/pin-icon';
import { LockIcon } from '../icons/lock-icon';
import MoreButton from '../more-button';
import { BulletSeparator as Bullet } from '../separator';
import PostTitle from '../post-title';
import TopCommentIconLink from '../top-comment-icon-link';
import TimeAgo from '../time-ago';
import Hashtags from '../hashtags';
import PostActions from '../../containers/post-actions';
import { getCategory } from '../../selectors/categories-selectors';
import withFontClassName from '../../hoc/with-font-class-name';
import withTranslate from '../../hoc/with-translate';
import withExperiment from '../../hoc/with-experiment';
import styles from './post-list-item-compact.scss';
import MemberCardAsync from '../../containers/member-card-async';
import {
  getIsListHashtagsEnabled,
  getIsViewsCountEnabled,
} from '../../selectors/app-settings-selectors';
import Link from '../link/internal-link';
import ProfileLink from '../link/profile-link';

const BulletSeparator = () => <Bullet className={'forum-text-color'} />;

export const PostListItemCompact = ({
  category,
  showCategoryLink,
  post,
  query,
  titleFontClassName,
  contentFontClassName,
  isViewsCountEnabled,
  isListHashtagsEnabled,
  isSnappyTurboEnable,
  fetchPostPageData,
  t,
}) => {
  const className = classNames(
    styles.container,
    'forum-text-color',
    'post-list-item',
    contentFontClassName,
  );
  const owner = post.owner || {};
  const moreButtonId = `more-button-${post._id}`;
  const hashtags = post.hashtags;
  const showHashtags = isListHashtagsEnabled && !isEmpty(hashtags);
  return (
    <tr id={post._id} className={className} data-hook="post-list-item">
      <td className={classNames('forum-text-color', styles.author)}>
        <div>
          <MemberCardAsync viewedMemberId={owner.siteMemberId} className={styles.memberCard}>
            <ProfileLink className={classNames('forum-text-color', styles.authorLink)} user={owner}>
              {owner.name}
            </ProfileLink>
          </MemberCardAsync>
        </div>
        <TimeAgo className={styles.timeAgo} time={post.createdDate} />
      </td>
      <td className={styles.titleCell}>
        <div className={styles.titleWrapper}>
          <Link
            onMouseEnter={() => isSnappyTurboEnable && fetchPostPageData(post.slug)}
            className={classNames(titleFontClassName, styles.titleLink)}
            to={`/${category.slug}/${post.slug}`}
          >
            <PostTitle
              className={classNames(
                styles.title,
                titleFontClassName,
                'forum-text-color',
                'forum-link-hover-color',
              )}
              title={post.title}
              query={query}
              type={PostTitle.SMALL}
            />
          </Link>
          {post.isPinned && (
            <PinIcon
              className={classNames(styles.icon, 'icon-secondary-fill')}
              isPinned={post.isPinned}
            />
          )}
          {post.isCommentsDisabled && (
            <LockIcon
              className={classNames(styles.icon, 'icon-secondary-fill')}
              isLocked={post.isCommentsDisabled}
            />
          )}
          {Boolean(post.topCommentId) && (
            <TopCommentIconLink
              t={t}
              topCommentId={post.topCommentId}
              linkComponent={Link}
              toolTipClassName={styles.tooltip}
            />
          )}
        </div>
        <div className={classNames(styles.statsRow, 'forum-links-hashtags-color')}>
          <span className={classNames('forum-text-color')}>
            {post.totalComments}&nbsp;&nbsp;{t('post-list-compact.comments')}
          </span>
          <BulletSeparator />
          <span className={classNames('forum-text-color')}>
            {post.likeCount}&nbsp;&nbsp;{t('post-list-compact.likes')}
          </span>
          {isViewsCountEnabled && <BulletSeparator />}
          {isViewsCountEnabled && (
            <span className={classNames('forum-text-color')}>
              {post.viewCount}&nbsp;&nbsp;{t('post-list-compact.views')}
            </span>
          )}
          {showCategoryLink && <BulletSeparator />}
          {showCategoryLink && (
            <Link
              className={classNames('forum-text-color', 'forum-link-hover-color')}
              to={`/${category.slug}`}
            >
              {category.label}
            </Link>
          )}
          {showHashtags && <BulletSeparator />}
          {showHashtags && <Hashtags hashtags={hashtags} className={styles.hashtags} />}
        </div>
      </td>
      <td className={styles.actions}>
        <MoreButton id={moreButtonId}>
          <PostActions post={post} focusOnCloseId={moreButtonId} />
        </MoreButton>
      </td>
    </tr>
  );
};

PostListItemCompact.propTypes = {
  t: PropTypes.func,
  category: PropTypes.object,
  post: PropTypes.object.isRequired,
  query: PropTypes.string,
  titleFontClassName: PropTypes.string,
  contentFontClassName: PropTypes.string,
  navigation: PropTypes.object,
  showCategoryLink: PropTypes.bool,
  isViewsCountEnabled: PropTypes.bool.isRequired,
  isListHashtagsEnabled: PropTypes.bool.isRequired,
  isSnappyTurboEnable: PropTypes.bool.isRequired,
  fetchPostPageData: PropTypes.func.isRequired,
};

const mapRuntimeToProps = (state, ownProps, actions) => ({
  category: getCategory(state, ownProps.post.categoryId),
  isViewsCountEnabled: getIsViewsCountEnabled(state),
  isListHashtagsEnabled: getIsListHashtagsEnabled(state),
  fetchPostPageData: actions.fetchPostPageData,
});

export default flowRight(
  connect(mapRuntimeToProps),
  withExperiment({ isSnappyTurboEnable: EXPERIMENT_SNAPPY_TURBO }),
  withFontClassName,
  withTranslate,
)(PostListItemCompact);
